/**
 * La vara de la charla asistida (encargo 99, diseño de #468): ¿el alumno dijo la
 * respuesta modelo? La usan el backend (decide si el turno va al coach) y speak
 * (karaoke y qué palabras marcar), así que vive acá, pura y sin dependencias.
 *
 * Es la «tolerante» del prototipo (`casos.mjs` de la #468), portada tal cual:
 * - mismas palabras en el mismo orden, sin mayúsculas, puntuación ni muletillas
 *   («um», «uh»…), con las contracciones abiertas, y lo mismo escrito distinto
 *   igualado: cifras («2», «7:30», «$5»), fechas y años dichos como se dicen
 *   («June 1» = «June first», «2024» = «twenty twenty-four»; `canonicalNumbers`),
 *   guiones («ice-cream») y tildes;
 * - deja pasar UNA sola palabra chica (a, an, the, please) de más o de menos, y avisa;
 * - acepta un arranque cortado («Can I have a s-… Can I have a small coffee»): la
 *   frase entera, con la misma tolerancia, al FINAL de lo oído, si lo descartado
 *   arranca como la frase y no es más largo que ella.
 * Nunca dice «está mal»: o se acepta, o se pide repetir.
 *
 * El repaso de correcciones suma una regla (`compareCorrectionReview`, el
 * `compararCorreccion` de la #468): lo que la corrección cambió no se perdona,
 * aunque sea una palabra chica, y se detecta si volvió a decirla como antes.
 *
 * El `vara` del A0 (`a0PhraseMatch` en packages/modules/languages) es la misma regla
 * pero sólo devuelve aprobado o no; ésta además dice QUÉ palabras marcar.
 */

const FILLERS = new Set(['um', 'uh', 'eh', 'er', 'erm', 'hmm', 'mm']);
/**
 * Ruidos e interjecciones, con cualquier alargue: «ah», «ahhh», «ohh», «ummm»,
 * «hmmm», «aww», «wow», «oops», «haha». No son inglés para practicar (Emmanuel,
 * 20/10: «"ahh" en lugar de "ahhh"» no tiene sentido): no cuentan ni de más ni de
 * menos, ni en la charla asistida ni en el repaso.
 */
const INTERJECTION =
  /^(?:a+h+|a+w+|o+h+|u+h+m*|u+m+|h+m+|m{2,}|e+h+|e+r+m*|(?:h+a+){2,}|w+o+w+|o+o*p+s+|u+g+h+|e+w+)$/;

/** ¿La palabra (como se escribe o se oye) es un ruido o una interjección? */
export function isInterjection(word: string): boolean {
  const k = word
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z]+/g, '');
  return k.length > 0 && INTERJECTION.test(k);
}

/** ¿El texto tiene alguna palabra que es un ruido o una interjección? */
export function hasInterjection(text: string): boolean {
  return text.split(/[\s,.;:!?…]+/).some(isInterjection);
}
/** Las únicas palabras que pueden sobrar o faltar (una sola) sin pedir repetir. */
const SMALL_WORDS = new Set(['a', 'an', 'the', 'please']);
const CONTRACTIONS: Readonly<Record<string, string>> = {
  "i'm": 'i am',
  "can't": 'can not',
  cannot: 'can not',
  "won't": 'will not',
  "shan't": 'shall not',
  "let's": 'let us',
};
/** Con «'s» = «is» (en el resto, «'s» es posesivo y queda como está). */
const IS_CONTRACTED = new Set([
  'it',
  'that',
  'what',
  'there',
  'here',
  'he',
  'she',
  'who',
  'where',
  'how',
  'when',
  'why',
]);
/** Terminaciones contraídas que se abren igual en cualquier palabra. */
const SUFFIXES: ReadonlyArray<readonly [string, string]> = [
  ["n't", ' not'],
  ["'ll", ' will'],
  ["'re", ' are'],
  ["'ve", ' have'],
  ["'d", ' would'],
];

const ONES = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

/** 0–999 en palabras. */
function below1000Words(n: number): string {
  if (n < 20) return ONES[n]!;
  if (n < 100) return n % 10 === 0 ? TENS[n / 10]! : `${TENS[Math.floor(n / 10)]} ${ONES[n % 10]}`;
  const rest = n % 100;
  return `${ONES[Math.floor(n / 100)]} hundred${rest ? ` ${below1000Words(rest)}` : ''}`;
}

/** 0–999.999.999 en palabras («25» → «twenty five», «50000» → «fifty thousand»); `null` fuera de rango. */
function numberWords(n: number): string | null {
  if (!Number.isInteger(n) || n < 0 || n > 999_999_999) return null;
  if (n < 1000) return below1000Words(n);
  const millions = Math.floor(n / 1_000_000);
  const thousands = Math.floor(n / 1000) % 1000;
  const rest = n % 1000;
  return [
    millions ? `${below1000Words(millions)} million` : '',
    thousands ? `${below1000Words(thousands)} thousand` : '',
    rest ? below1000Words(rest) : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/** El ordinal de un número en palabras: «twenty one» → «twenty first». */
function ordinalWords(n: number): string | null {
  const words = numberWords(n);
  if (words === null) return null;
  const parts = words.split(' ');
  const last = parts.pop()!;
  const irregular: Readonly<Record<string, string>> = {
    one: 'first',
    two: 'second',
    three: 'third',
    five: 'fifth',
    eight: 'eighth',
    nine: 'ninth',
    twelve: 'twelfth',
  };
  const ord = irregular[last] ?? (last.endsWith('y') ? `${last.slice(0, -1)}ieth` : `${last}th`);
  return [...parts, ord].join(' ');
}

/** Un número escrito con cifras, con separador de miles o decimales: «1,000», «99.9». */
function digitsWords(k: string): string | null {
  const m = /^(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?$/.exec(k);
  if (!m) return null;
  const whole = numberWords(Number(m[1]!.replace(/,/g, '')));
  if (whole === null) return null;
  // «99.9» = «ninety nine point nine»: los decimales, de a una cifra.
  return m[2] ? `${whole} point ${[...m[2]].map((d) => ONES[Number(d)]).join(' ')}` : whole;
}

/**
 * Una cifra escrita como se dice: «7:30» → «seven thirty», «$5» → «five dollars», «21st» →
 * «twenty first», «1,000» → «one thousand», «8x10» → «eight by ten», «35°C» → «thirty five
 * degrees celsius», «5K» → «five k».
 */
function spokenNumber(k: string, dollars: boolean): string | null {
  const ordinal = /^(\d+)(?:st|nd|rd|th)$/.exec(k);
  if (ordinal) return ordinalWords(Number(ordinal[1]));
  const time = /^(\d{1,2}):(\d{2})$/.exec(k);
  if (time) {
    const h = numberWords(Number(time[1]));
    const m = Number(time[2]);
    if (h === null) return null;
    if (m === 0) return `${h} o'clock`;
    return m < 10 ? `${h} oh ${ONES[m]}` : `${h} ${numberWords(m)}`;
  }
  const by = /^([\d,.]+)[x×]([\d,.]+)$/.exec(k);
  if (by) {
    const [a, b] = [digitsWords(by[1]!), digitsWords(by[2]!)];
    return a !== null && b !== null ? `${a} by ${b}` : null;
  }
  const degrees = /^([\d,.]+)°([cf])$/.exec(k);
  if (degrees) {
    const n = digitsWords(degrees[1]!);
    if (n === null) return null;
    return `${n} ${n === 'one' ? 'degree' : 'degrees'} ${degrees[2] === 'c' ? 'celsius' : 'fahrenheit'}`;
  }
  const unit = /^(\d+)([a-z]+)$/.exec(k);
  if (unit) {
    const n = numberWords(Number(unit[1]));
    return n === null ? null : `${n} ${unit[2]}`;
  }
  const words = digitsWords(k);
  if (words === null) return null;
  return dollars ? `${words} ${k === '1' ? 'dollar' : 'dollars'}` : words;
}

/** Una palabra visible → sus tokens comparables (0 si es muletilla o puntuación suelta). */
function wordTokens(raw: string): string[] {
  const plain = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[’‘]/g, "'");
  const dollars = /^\$\d/.test(plain);
  const percent = /\d%[^0-9a-z]*$/.test(plain);
  // «(%)» suelto se lee «percent» (y «percent percent» cuenta una vez, en `canonicalNumbers`).
  if (/^[^0-9a-z]*%[^0-9a-z]*$/.test(plain)) return ['percent'];
  // El apóstrofo final suelto (el posesivo plural «teachers’») no se oye: «teachers» (card #568, 20/10:
  // el ejemplo de una palabra guardada no pasaba nunca). El de adentro queda («don't», «she's»).
  // Las rayas («bandwidth—the», «2–1») separan como el guion.
  const k = plain
    .replace(/[–—]/g, '-')
    .replace(/^[^0-9a-z]+/i, '')
    .replace(/[^0-9a-z'°]+$/i, '')
    .replace(/'+$/, '');
  // «oh» queda: en «twenty oh five» es parte del número (`canonicalNumbers` lo saca después).
  if (!k || FILLERS.has(k) || (k !== 'oh' && INTERJECTION.test(k))) return [];
  // «ice-cream» = «ice cream»: el guion de adentro separa palabras. Entre dos cifras («18-24»,
  // «2–1») queda una marca de rango, que `canonicalNumbers` iguala a «to» y después saca.
  if (k.includes('-')) {
    const parts = k.split('-');
    return parts.flatMap((part, i) => [
      ...(i > 0 && /\d$/.test(parts[i - 1]!) && /^\d/.test(part) ? [RANGE] : []),
      ...wordTokens(part),
    ]);
  }
  // «9 a.m.» = «9 AM».
  if (k === 'a.m' || k === 'p.m') return [k[0] + 'm'];
  const spoken = spokenNumber(k, dollars);
  if (spoken !== null) return [...spoken.split(' '), ...(percent ? ['percent'] : [])];
  return openContraction(k).split(' ');
}

/** La marca de un rango escrito con guion entre cifras («18-24»): `canonicalNumbers` la saca. */
const RANGE = '-';

function openContraction(k: string): string {
  const fixed = CONTRACTIONS[k];
  if (fixed) return fixed;
  if (k.endsWith("'s") && IS_CONTRACTED.has(k.slice(0, -2))) return `${k.slice(0, -2)} is`;
  for (const [suffix, open] of SUFFIXES) {
    if (k.endsWith(suffix) && k.length > suffix.length) return k.slice(0, -suffix.length) + open;
  }
  return k;
}

/** Los números en palabras, para leerlos: «one» → 1, «first» → 1 ordinal. */
const CARDINAL = new Map<string, number>([
  ...ONES.map((w, i) => [w, i] as const),
  ...TENS.flatMap((w, i) => (w ? [[w, i * 10] as const] : [])),
]);
const ORDINAL = new Map<string, number>(
  [...CARDINAL.entries()].filter(([, n]) => n > 0).map(([, n]) => [ordinalWords(n)!, n] as const),
);
const MONTHS = new Set([
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
  'jan',
  'feb',
  'apr',
  'jun',
  'jul',
  'aug',
  'sep',
  'sept',
  'oct',
  'nov',
  'dec',
]);

/** Un token del texto, con la palabra visible de la que salió. */
interface Tok {
  t: string;
  /** Índice en {@link assistedVisibleWords}. */
  w: number;
  /** ¿Salió de una cifra escrita («1», «2024», «1st»)? */
  digits: boolean;
}

/** Un número leído de los tokens `[from, to)`. */
interface NumberRun {
  from: number;
  to: number;
  value: number;
  ordinal: boolean;
  digits: boolean;
}

/**
 * Lee un número de 0 a 999.999.999 en palabras desde `i` («twenty four», «two
 * thousand and five», «a hundred», «fifty thousand», «twenty first»), lo más largo posible; `null` si en `i`
 * no empieza uno.
 */
function readNumber(toks: readonly Tok[], i: number): NumberRun | null {
  const at = (j: number) => toks[j]?.t;
  let j = i;
  let ordinal = false;
  /** 0–99 desde `j`: «twenty», «twenty four», «twenty first», «seven». */
  const below100 = (): number | null => {
    const t = at(j);
    if (t === undefined) return null;
    const tens = CARDINAL.get(t);
    if (tens !== undefined && tens >= 20) {
      j++;
      const u = at(j);
      const one = u === undefined ? undefined : CARDINAL.get(u);
      if (one !== undefined && one >= 1 && one <= 9) {
        j++;
        return tens + one;
      }
      const ord = u === undefined ? undefined : ORDINAL.get(u);
      if (ord !== undefined && ord >= 1 && ord <= 9) {
        j++;
        ordinal = true;
        return tens + ord;
      }
      return tens;
    }
    if (tens !== undefined) {
      j++;
      return tens;
    }
    const ord = ORDINAL.get(t);
    if (ord !== undefined) {
      j++;
      ordinal = true;
      return ord;
    }
    return null;
  };
  /** «and» entre «hundred»/«thousand» y lo que sigue («one hundred and five»). */
  const skipAnd = () => {
    if (at(j) !== 'and') return;
    const save = j;
    j++;
    const peek = at(j);
    if (peek === undefined || (!CARDINAL.has(peek) && !ORDINAL.has(peek))) j = save;
  };
  /** 0–999 desde `j`, con «hundred». */
  const below1000 = (): number | null => {
    let head = below100();
    if (head === null && at(j) === 'a' && (at(j + 1) === 'hundred' || at(j + 1) === 'hundredth')) {
      j++;
      head = 1;
    }
    if (head === null || ordinal) return head;
    if (head >= 1 && head <= 99 && (at(j) === 'hundred' || at(j) === 'hundredth')) {
      ordinal = at(j) === 'hundredth';
      j++;
      if (ordinal) return head * 100;
      skipAnd();
      const save = j;
      const rest = below100();
      if (rest === null) j = save;
      return head * 100 + (rest ?? 0);
    }
    return head;
  };
  let value: number | null = null;
  let seg = below1000();
  for (const [word, mult] of [
    ['million', 1_000_000],
    ['thousand', 1000],
  ] as const) {
    if (seg === null && at(j) === 'a' && (at(j + 1) === word || at(j + 1) === `${word}th`)) {
      j++;
      seg = 1;
    }
    if (ordinal || seg === null || seg < 1 || seg > 999) continue;
    if (at(j) !== word && at(j) !== `${word}th`) continue;
    if (value !== null && value % (mult * 1000) !== 0) continue;
    ordinal = at(j) === `${word}th`;
    j++;
    value = (value ?? 0) + seg * mult;
    seg = null;
    if (ordinal) break;
    skipAnd();
    const save = j;
    seg = below1000();
    if (seg === null) j = save;
  }
  if (seg !== null) value = (value ?? 0) + seg;
  if (value === null) return null;
  const digits = toks.slice(i, j).every((x) => x.digits);
  return { from: i, to: j, value, ordinal, digits };
}

/**
 * Los números dichos de varias formas, a una sola (E2 de la #600, 21/10: «June
 * first» no pasaba por «June 1»). Sobre la frase entera, no palabra por palabra:
 * - un número de dos cifras seguido de otro, o de «oh» y una cifra, es un año:
 *   «twenty twenty four» = «two thousand (and) twenty four» = «2024»;
 * - una fecha escrita con cifra se dice con ordinal: «June 1» = «June first» =
 *   «June 1st» = «June the first» (también «1 June»). «June one» dicho en
 *   palabras NO: es mal inglés, y el repaso de una corrección «June one» →
 *   «June first» lo tiene que distinguir.
 * El texto de los dos lados pasa por acá, así que todo número queda escrito
 * igual: «a hundred» = «one hundred» = «100».
 */
function canonicalNumbers(toks: readonly Tok[]): Tok[] {
  // 1. Los números, de izquierda a derecha.
  const runs: NumberRun[] = [];
  for (let i = 0; i < toks.length; ) {
    const run = readNumber(toks, i);
    if (run && run.to > i) {
      runs.push(run);
      i = run.to;
    } else i++;
  }
  // 2. Las fechas escritas con cifra: el día va en ordinal.
  for (const run of runs) {
    if (!run.digits || run.ordinal || run.value < 1 || run.value > 31) continue;
    const before = toks[run.from - 1]?.t;
    const beforeThe = toks[run.from - 2]?.t;
    const after = toks[run.to]?.t;
    const month =
      (before !== undefined && MONTHS.has(before)) ||
      (before === 'the' && beforeThe !== undefined && MONTHS.has(beforeThe)) ||
      (after !== undefined && MONTHS.has(after));
    if (month) run.ordinal = true;
  }
  // 3. Los años de a pares: «nineteen ninety», «twenty oh five».
  const merged: NumberRun[] = [];
  for (let k = 0; k < runs.length; k++) {
    const a = runs[k]!;
    const b = runs[k + 1];
    const pair = (from: number, value: number, to: number, digits: boolean): void => {
      merged.push({ from, to, value: a.value * 100 + value, ordinal: false, digits });
    };
    if (!a.ordinal && a.value >= 10 && a.value <= 99) {
      const oh = toks[a.to]?.t === 'oh' ? CARDINAL.get(toks[a.to + 1]?.t ?? '') : undefined;
      if (oh !== undefined && oh >= 1 && oh <= 9) {
        pair(a.from, oh, a.to + 2, false);
        if (b && b.from === a.to + 1) k++;
        continue;
      }
      if (b && b.from === a.to && !b.ordinal && b.value >= 10 && b.value <= 99) {
        pair(a.from, b.value, b.to, a.digits && b.digits);
        k++;
        continue;
      }
    }
    merged.push(a);
  }
  // 3b. «18-24» = «eighteen to twenty four» = «18 to 24»: el «to» o la marca de rango
  //     entre dos números no cuenta (el resultado «2–1» se dice «two to one» o «two one»).
  const drop = new Set<number>();
  for (let k = 0; k + 1 < merged.length; k++) {
    const sep = toks[merged[k]!.to];
    if (merged[k + 1]!.from === merged[k]!.to + 1 && (sep?.t === 'to' || sep?.t === RANGE))
      drop.add(merged[k]!.to);
  }
  // 4. Cada número, escrito en sus palabras de siempre.
  type Piece = { toks: Tok[]; day: boolean };
  const pieces: Piece[] = [];
  let i = 0;
  for (const run of merged) {
    for (; i < run.from; i++) if (!drop.has(i)) pieces.push({ toks: [toks[i]!], day: false });
    const words = (run.ordinal ? ordinalWords(run.value) : numberWords(run.value))!.split(' ');
    const span = toks.slice(run.from, run.to);
    pieces.push({
      toks: words.map((t, n) => {
        const from = span[Math.floor((n * span.length) / words.length)]!;
        return { t, w: from.w, digits: run.digits };
      }),
      day: run.ordinal && run.value >= 1 && run.value <= 31,
    });
    i = run.to;
  }
  for (; i < toks.length; i++) pieces.push({ toks: [toks[i]!], day: false });
  // 5. La fecha, siempre «mes + día»: «the first of June», «1 June», «June the
  //    first» = «June first».
  const word = (k: number) => (pieces[k]?.day ? undefined : pieces[k]?.toks[0]?.t);
  const isMonth = (k: number) => {
    const t = word(k);
    return t !== undefined && MONTHS.has(t);
  };
  const out: Tok[] = [];
  for (let k = 0; k < pieces.length; k++) {
    const p = pieces[k]!;
    if (p.day && word(k - 1) === 'the' && isMonth(k - 2)) {
      // «June the first»: el «the» ya salió; se saca.
      out.pop();
      out.push(...p.toks);
      continue;
    }
    if (p.day && !isMonth(k - 1)) {
      const of = word(k + 1) === 'of' ? 1 : 0;
      if (isMonth(k + 1 + of)) {
        if (word(k - 1) === 'the') out.pop();
        out.push(...pieces[k + 1 + of]!.toks, ...p.toks);
        k += 1 + of;
        continue;
      }
    }
    out.push(...p.toks);
  }
  // 6. El «oh» de «seven oh five» o de «twenty oh five» se dice o no: no cuenta.
  return out.filter(
    (x, n) => x.t !== 'oh' && x.t !== RANGE && !(x.t === 'percent' && out[n - 1]?.t === 'percent'),
  );
}

/** Los tokens comparables de un texto, con la palabra visible de cada uno. */
function textTokens(text: string): Tok[] {
  const toks = assistedVisibleWords(text).flatMap((raw, w) => {
    const digits = /\d/.test(raw);
    return wordTokens(raw).map((t) => ({ t, w, digits }));
  });
  return canonicalNumbers(toks);
}

/** Tokens comparables de un texto. */
export function assistedTokens(text: string): string[] {
  return textTokens(text).map((x) => x.t);
}

/** Cuántos tokens tiene cada palabra visible del texto (0 = puntuación suelta o muletilla). */
function tokensPerWord(text: string): number[] {
  const counts = assistedVisibleWords(text).map(() => 0);
  for (const x of textTokens(text)) counts[x.w]!++;
  return counts;
}

/** Palabras visibles de un texto (como se pintan en la tarjeta: cortadas por espacios). */
export function assistedVisibleWords(text: string): string[] {
  return text.split(/\s+/).filter((w) => w.length > 0);
}

/** LCS palabra a palabra: qué índices de `a` y de `b` quedan fuera de la subsecuencia común. */
export function assistedDiff(
  a: readonly string[],
  b: readonly string[],
): { aOnly: Set<number>; bOnly: Set<number> } {
  const n = a.length;
  const m = b.length;
  const lcs = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] =
        a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const aOnly = new Set<number>();
  const bOnly = new Set<number>();
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) aOnly.add(i++);
    else bOnly.add(j++);
  }
  while (i < n) aOnly.add(i++);
  while (j < m) bOnly.add(j++);
  return { aOnly, bOnly };
}

/** Índices de token de la frase → índices de palabra VISIBLE (para subrayar en la tarjeta). */
function visibleIndices(text: string, tokenIdx: ReadonlySet<number>): number[] {
  const out = new Set<number>();
  textTokens(text).forEach((x, k) => {
    if (tokenIdx.has(k)) out.add(x.w);
  });
  return [...out].sort((a, b) => a - b);
}

/**
 * Las palabras visibles `visible` (índices de {@link assistedVisibleWords}) de
 * `text`, por tramos seguidos y sin la puntuación de los bordes: para decir qué
 * faltó como se lee en la tarjeta («I'd like», no «would like») y sin pegar
 * palabras sueltas en una sola cita (encargo 113: «hot please can get large one»).
 */
export function assistedWordChunks(text: string, visible: readonly number[]): string[] {
  const words = assistedVisibleWords(text);
  const clean = (w: string): string => w.replace(/^[^\p{L}\p{N}']+|[^\p{L}\p{N}']+$/gu, '');
  const chunks: string[][] = [];
  let prev = -2;
  for (const i of [...new Set(visible)].sort((a, b) => a - b)) {
    const w = words[i] === undefined ? '' : clean(words[i]);
    if (w === '') continue;
    if (i === prev + 1 && chunks.length > 0) chunks[chunks.length - 1]!.push(w);
    else chunks.push([w]);
    prev = i;
  }
  return chunks.map((c) => c.join(' '));
}

export type AssistedMatchState =
  /** Tal cual. */
  | 'ok'
  /** Con una palabra chica de más o de menos: se acepta y se avisa cuál. */
  | 'ok-warning'
  /** Con un arranque cortado delante: se acepta. */
  | 'ok-restart'
  /** No coincide: «Repetila» o «Seguir». */
  | 'repeat'
  /** No se oyó nada. */
  | 'silence';

export interface AssistedMatch {
  state: AssistedMatchState;
  /** ¿Se acepta? (`ok`, `ok-warning` u `ok-restart`). */
  accepted: boolean;
  /** Palabras VISIBLES de la frase modelo que faltaron o cambiaron (para subrayar). */
  marks: number[];
  /** Tokens de la frase que no se oyeron. */
  missing: string[];
  /** Tokens oídos que la frase no tiene. */
  extra: string[];
  /** Con arranque cortado: los tokens descartados del comienzo. */
  restart?: string[];
}

/** `heard` ya en tokens: el arranque cortado prueba con un pedazo (sin volver a leer sus números). */
function matchBase(model: string, heard: string | readonly string[]): AssistedMatch {
  const m = assistedTokens(model);
  const o = typeof heard === 'string' ? assistedTokens(heard) : heard;
  if (o.length === 0) {
    return { state: 'silence', accepted: false, marks: [], missing: [], extra: [] };
  }
  const { aOnly, bOnly } = assistedDiff(m, o);
  const missing = [...aOnly].map((i) => m[i]!);
  const extra = [...bOnly].map((j) => o[j]!);
  const marks = visibleIndices(model, aOnly);
  if (missing.length === 0 && extra.length === 0) {
    return { state: 'ok', accepted: true, marks, missing, extra };
  }
  const only = missing[0] ?? extra[0]!;
  if (missing.length + extra.length === 1 && SMALL_WORDS.has(only)) {
    return { state: 'ok-warning', accepted: true, marks, missing, extra };
  }
  return { state: 'repeat', accepted: false, marks, missing, extra };
}

/** ¿Lo oído (`heard`) es la frase modelo (`model`)? */
export function compareAssisted(model: string, heard: string): AssistedMatch {
  const r = matchBase(model, heard);
  if (r.accepted || r.state === 'silence') return r;
  // El arranque cortado es un arranque DE LA FRASE: lo descartado empieza como
  // ella («C-… can I have…») y no es más largo. Lo propio del alumno seguido
  // del final de la frase («No, I don't want anything. Yes») no es decirla.
  const o = assistedTokens(heard);
  const first = assistedTokens(model)[0] ?? '';
  const maxRestart = assistedTokens(model).length;
  if (o.length === 0 || !first.startsWith(o[0]!)) return r;
  for (let k = 1; k < o.length && k <= maxRestart; k++) {
    const tail = matchBase(model, o.slice(k));
    if (tail.accepted) return { ...tail, state: 'ok-restart', restart: o.slice(0, k) };
  }
  return r;
}

/**
 * ¿Dijo OTRA frase? Se oyó menos de la mitad de la frase modelo (`missing` =
 * sus tokens que no se oyeron): ahí «Casi» y «te faltó …» no le dicen nada
 * (encargo 113: «How much does it cost?» por «I'd like it with milk» decía
 * «Casi. Te faltó «would like»»).
 */
export function assistedOtherPhrase(model: string, missing: readonly string[]): boolean {
  const n = assistedTokens(model).length;
  return n > 0 && (n - missing.length) * 2 < n;
}

/**
 * Palabras de función: no dicen que el alumno intentó ESTA frase (cualquier
 * otra las tiene: «I want a pizza with cheese» comparte «I», «a» y «with» con
 * «A medium, please. Can I get it with milk?»).
 */
const FUNCTION_WORDS = new Set([
  'a',
  'an',
  'the',
  'please',
  'i',
  'you',
  'he',
  'she',
  'it',
  'we',
  'they',
  'me',
  'my',
  'your',
  'is',
  'am',
  'are',
  'was',
  'were',
  'be',
  'do',
  'does',
  'did',
  'have',
  'has',
  'can',
  'could',
  'will',
  'would',
  'to',
  'of',
  'in',
  'on',
  'at',
  'for',
  'with',
  'and',
  'or',
  'but',
  'so',
  'that',
  'this',
  'not',
  'what',
  'yes',
  'no',
  'okay',
  'ok',
]);

/**
 * ¿Lo oído es un INTENTO de decir la frase modelo, aunque salga mal? (encargo
 * 582, Emmanuel 21/10: un «Ahem» o algo sin relación contaba como intento, y
 * «Seguir» la daba por practicada con ayuda en vez de mandarla al Repaso del
 * día como salteada.) Cuenta las palabras con contenido de la frase (las
 * visibles, así «4522» es una sola aunque se oiga «45228891») que se oyeron en
 * orden, y pide al menos un cuarto de ellas, y siempre una. Medido sobre 122
 * intentos rechazados de las copias de la #524 y siguientes: deja afuera los 52
 * ruidos o frases sin relación, y cuenta los 58 intentos con errores, también
 * «I wanted more road» (2 de 9) por «I wanted more growth. Can I ask what
 * growth looks like here?».
 */
export function assistedAttempted(model: string, heard: string): boolean {
  const byWord = assistedVisibleWords(model).map((_, i) => ({ i, tokens: [] as string[] }));
  for (const x of textTokens(model)) byWord[x.w]!.tokens.push(x.t);
  const content = byWord.filter((w) => w.tokens.some((t) => !FUNCTION_WORDS.has(t)));
  if (content.length === 0) return assistedTokens(heard).length > 0;
  const heardIdx = new Set(assistedHeardWords(model, heard));
  const got = content.filter((w) => heardIdx.has(w.i)).length;
  return got >= Math.max(1, Math.floor(content.length / 4));
}

export type CorrectionReviewState =
  | AssistedMatchState
  /** La dijo igual que en la charla (sin la corrección). */
  | 'same-as-before'
  /** Le faltó justo lo que se corrigió, o volvió lo que se sacó. */
  | 'missing-correction'
  /**
   * Dicha de otra forma que igual demuestra la corrección («he» por «she», sin
   * sujeto…): la exacta la rechazó y la aceptó el juez del server.
   */
  | 'ok-judged';

export interface CorrectionReviewMatch extends Omit<AssistedMatch, 'state'> {
  state: CorrectionReviewState;
  /**
   * Con `missing-correction`: lo corregido que faltó, como se lee en «Mejor
   * así», por tramos ({@link assistedWordChunks}).
   */
  missingCorrection?: string[];
  /** Con `missing-correction`: lo que la corrección sacó y volvió. */
  cameBack?: string[];
  /** Con `ok-judged`: por qué la aceptó el juez (para el log, en inglés). */
  judgedReason?: string;
  /**
   * Con `ok-judged` sin la forma guardada: qué hace distinto la guardada, para
   * «También podrías decir «…», <tip>» (Emmanuel, 15/10).
   */
  judgedTip?: string;
  /**
   * Con `ok-judged`: lo practicado estuvo bien pero el resto trajo un error
   * NUEVO (Emmanuel, 20/10: «I'd like an improve»). Pasa igual y se avisa,
   * «¡Bien el "<judgedPoint>"! Ojo: <judgedSlip>.», sin castigar.
   */
  judgedSlip?: string;
  /** Con `judgedSlip`: lo practicado, en inglés («I'd like»). */
  judgedPoint?: string;
}

/**
 * Lo que la corrección cambió, en índices de tokens de `better`: lo que agregó o
 * cambió (fuera del LCS) y, si MOVIÓ una palabra, también las que cruzó. En «How
 * much it is?» → «How much is it?» el LCS se queda con «how much is» y sólo «it»
 * quedaba como cambiado: «Te faltó «it»» por un «How much» (encargo 113). Una
 * palabra movida tampoco es una que la corrección sacó (`removed`).
 */
function correctionChange(
  before: readonly string[],
  better: readonly string[],
): { changed: Set<number>; removed: Set<string> } {
  const { aOnly, bOnly } = assistedDiff(before, better);
  // El LCS empareja en orden: el k-ésimo emparejado de cada lado van juntos.
  const aMatched = before.map((_, i) => i).filter((i) => !aOnly.has(i));
  const bMatched = better.map((_, j) => j).filter((j) => !bOnly.has(j));
  const changed = new Set(bOnly);
  const moved = new Set<number>();
  for (const j of [...bOnly].sort((x, y) => x - y)) {
    const i = [...aOnly]
      .sort((x, y) => x - y)
      .find((k) => before[k] === better[j] && !moved.has(k));
    if (i === undefined) continue; // agregada, no movida
    moved.add(i);
    // Las que cruzó: su orden respecto de la movida cambió de un lado al otro.
    bMatched.forEach((k, n) => {
      if (k < j !== aMatched[n]! < i) changed.add(k);
    });
  }
  const removed = new Set([...aOnly].filter((i) => !moved.has(i)).map((i) => before[i]!));
  return { changed, removed };
}

/**
 * El repaso de una corrección: la tolerante, más «lo que la corrección cambió no se
 * perdona». `said` = lo que dijo en la charla, `corrected` = «Mejor así».
 */
export function compareCorrectionReview(
  correction: { said: string; corrected: string },
  heard: string,
): CorrectionReviewMatch {
  const r: CorrectionReviewMatch = compareAssisted(correction.corrected, heard);
  if (r.state === 'silence') return r;
  const better = assistedTokens(correction.corrected);
  const before = assistedTokens(correction.said);
  const o = assistedTokens(heard);
  const { changed, removed } = correctionChange(before, better);
  const from = r.state === 'ok-restart' ? (r.restart?.length ?? 0) : 0;
  const d = assistedDiff(better, o.slice(from));
  if (d.aOnly.size === 0 && d.bOnly.size === 0) return r;
  if (o.join(' ') === before.join(' ')) {
    return { ...r, state: 'same-as-before', accepted: false };
  }
  // Otra frase: no es que le faltó lo corregido.
  if (assistedOtherPhrase(correction.corrected, r.missing)) return r;
  const missingCorrection = assistedWordChunks(
    correction.corrected,
    visibleIndices(correction.corrected, new Set([...changed].filter((i) => d.aOnly.has(i)))),
  );
  const cameBack = [...d.bOnly].map((j) => o[from + j]!).filter((w) => removed.has(w));
  if (missingCorrection.length > 0 || cameBack.length > 0) {
    return { ...r, state: 'missing-correction', accepted: false, missingCorrection, cameBack };
  }
  return r;
}

/** Palabras visibles de «Mejor así» que la corrección agregó, cambió o movió (para encenderlas). */
export function correctionChangedWords(correction: { said: string; corrected: string }): number[] {
  const { changed } = correctionChange(
    assistedTokens(correction.said),
    assistedTokens(correction.corrected),
  );
  return visibleIndices(correction.corrected, changed);
}

/**
 * Karaoke: qué palabras visibles de la frase modelo ya aparecen en lo que el
 * transcriptor oyó hasta ahora (su parcial), con el mismo diff de la vara.
 */
export function assistedHeardWords(model: string, partial: string): number[] {
  const m = assistedTokens(model);
  const { aOnly } = assistedDiff(m, assistedTokens(partial));
  const heard = new Set<number>();
  for (let i = 0; i < m.length; i++) if (!aOnly.has(i)) heard.add(i);
  const all = tokensPerWord(model).map((n) => n > 0);
  textTokens(model).forEach((x, k) => {
    if (!heard.has(k)) all[x.w] = false;
  });
  return all.flatMap((ok, idx) => (ok ? [idx] : []));
}

/**
 * Karaoke EN ORDEN: cuántas palabras visibles de la frase, desde el principio,
 * se encienden con lo oído hasta ahora (un prefijo, nunca huecos). La escucha en
 * vivo suele errarle a la primera palabra (llega tarde y la adivina: «It's
 * starting to go really well» por «started to go really well», Emmanuel, 16/10),
 * y encender sólo lo oído dejaba un hueco al principio con el resto encendido.
 *
 * De izquierda a derecha con lo oído de `assistedHeardWords`: una palabra oída
 * extiende el prefijo hasta ella; UNA sin oír entre oídas (también la primera)
 * se llena igual cuando llega la siguiente oída — mal transcripta, o dicha
 * distinto —; DOS seguidas cortan, y lo de después no se enciende (si dice sólo
 * el final, no se encienden las de antes). Las palabras visibles sin tokens
 * (puntuación suelta) no cuentan como hueco. Sólo para mostrar: lo que decide
 * sigue siendo la vara (`compareAssisted` y compañía).
 */
export function assistedHeardInOrder(model: string, partial: string): number {
  const heard = new Set(assistedHeardWords(model, partial));
  const counts = tokensPerWord(model);
  let shown = 0;
  let gap = 0;
  for (let i = 0; i < counts.length; i++) {
    if (heard.has(i)) {
      shown = i + 1;
      gap = 0;
    } else if (counts[i]! > 0 && ++gap > 1) break;
  }
  return shown;
}

/** Sílabas aproximadas de una palabra en inglés (grupos de vocales, sin la «e» muda final). */
export function englishSyllables(word: string): number {
  let w = word.toLowerCase().replace(/[^a-z]/g, '');
  if (!w) return 0;
  if (w.length <= 3) return 1;
  w = w.replace(/(?:[^laeiouy]es|[^laeiouy]ed|[^laeiouy]e)$/, '').replace(/^y/, '');
  return Math.max(1, (w.match(/[aeiouy]{1,2}/g) ?? []).length);
}
